/* eslint-disable @typescript-eslint/no-explicit-any */
import { EditableComponent, ResponsiveGrid, EditableComponentProps } from '@adobe/aem-react-editable-components';
import React, { FC, useMemo, useState, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { respGridUtil, useEditorMode, renditions } from '@marriott/mi-ui-library';

import { TabbedLayoutCompProps, TabbedLayoutProps } from './TabbedLayout.types';
import { TabNavigation } from '../../molecules/Tabs/TabNavigation';
import { TabPanel } from '../../molecules/Tabs/TabPanel';
import { TabBanner } from '../../molecules/Tabs/TabBanner';
import { StyledTabbedLayout } from './TabbedLayout.styles';

const TabContainerConfig = {
  emptyLabel: 'Tabbed Layout',
  isEmpty: () => true,
  resourceType: `mi-aem-homepage-spa/components/content/tabbedlayout`,
};
export const TabbedLayout = (props: EditableComponentProps & TabbedLayoutProps) => (
  <EditableComponent config={{ ...TabContainerConfig }} {...props}>
    <TabbedLayoutComp {...props.model} isAuthorMode={props.isAuthorMode} />
  </EditableComponent>
);

const TabbedLayoutComp: FC<TabbedLayoutCompProps> = model => {
  const {
    styleclass,
    targetComponentName,
    trackingProperties,
    isAuthorMode,
    tabDetails = [],
    pagePath,
    itemPath,
    pageTitle,
  } = model;

  const { isInEditor } = useEditorMode();
  const theme = styleclass?.includes('inverse') ? 'dark' : 'light';
  const cqItems = respGridUtil(model)?.cqItems || {};

  /** Tabs Nav List */
  const tabItemList = useMemo(
    () =>
      tabDetails.map((tab, index) => ({
        tabValue: tab.nodeName,
        tabTitle: tab.tabName,
        tabIcon: tab.tabIcon?.split(':')[1] ?? '',
        tabTrackingDescription: tab.tabTrackingDescription,
        dynamicMedia: tab.dynamicMedia,
        customTabClickTrackValue: `${trackingProperties?.trackingContentPosition}|Tab${index + 1}-${
          tab.tabName
        }|internal`,
      })),
    [tabDetails, trackingProperties?.trackingContentPosition]
  );

  /** Selected tab state */
  const [selectedTab, setSelectedTab] = useState<string>(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const hashTab = window.location.hash.replace('#', '');
      const found = tabItemList.find(tab => tab.tabTitle === hashTab);
      if (found) return found.tabValue;
    }
    return tabItemList[0]?.tabValue;
  });

  useEffect(() => {
    if (!selectedTab) return;
    const el = document.getElementById(selectedTab);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [selectedTab]);

  /** Handle browser back/forward navigation */
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      const matchedTab = tabItemList.find(tab => tab.tabTitle === hash);
      if (matchedTab) {
        setSelectedTab(matchedTab.tabValue);
      } else {
        setSelectedTab(tabItemList[0]?.tabValue);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [tabItemList]);

  /** Handle tab selection */
  const onTabSelect = useCallback(
    (selectedTabValue: string) => {
      const tab = tabItemList.find(t => t.tabValue === selectedTabValue);
      if (!tab) return;

      setSelectedTab(selectedTabValue);
      window.history.pushState(null, '', `#${tab.tabTitle}`);
    },
    [tabItemList]
  );

  /** Selected tab dynamic media */
  const selectedTabDynamicMedia = useMemo(() => {
    return (
      tabItemList.find(tab => tab.tabValue === selectedTab)?.dynamicMedia ??
      (tabItemList[0]?.dynamicMedia as renditions)
    );
  }, [tabItemList, selectedTab]);

  /** Author mode panels */
  const authorModePanels = useMemo(() => {
    if (!isAuthorMode) return null;

    return tabDetails.map(tabDetail => (
      <ResponsiveGrid
        key={tabDetail.nodeName}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${tabDetail.nodeName}`}
        columnCount="12"
        gridClassNames=""
        customClassName=""
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-common-spa/components/container',
        }}
        isInEditor={isInEditor}
      />
    ));
  }, [isAuthorMode, tabDetails, pagePath, itemPath, isInEditor]);

  return (
    <StyledTabbedLayout
      className={clsx('m-container-fullbleed', styleclass)}
      data-target-component={targetComponentName || null}
      data-testid="homepage-TabbedLayout"
      data-component-name="o-homepage-TabbedLayout"
    >
      <TabBanner theme={theme} dynamicMedia={selectedTabDynamicMedia} />
      <TabNavigation
        selectedTab={selectedTab}
        setSelectedTab={onTabSelect}
        tabNavigationList={tabItemList}
        pageTitle={pageTitle}
      >
        {isAuthorMode ? (
          authorModePanels
        ) : (
          <TabPanel
            cqItems={cqItems}
            selectedTab={selectedTab}
            tabDetails={tabItemList.map(tab => ({ nodeName: tab.tabValue, tabName: tab.tabTitle }))}
          />
        )}
      </TabNavigation>
    </StyledTabbedLayout>
  );
};
